import type {
  AgendaItem,
  AgendaItemList,
  AgendaItemStatus,
} from "@elation/api-client/src/stitch-web";
import {
  type CreateAgendaItemMutationError,
  type DeleteAgendaItemMutationError,
  type UpdateAgendaItemMutationError,
  getListAgendaItemsQueryKey,
  useCreateAgendaItem,
  useDeleteAgendaItem,
  useUpdateAgendaItem,
} from "@elation/api-client/src/stitch-web";
import { useActor } from "@src/providers/actorProvider";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

// Monotonic counter for optimistic placeholder IDs. Module-level so multiple
// Agenda mounts in the same session don't collide. Real server IDs are
// positive; placeholders are negative.
let tempIdCounter = 0;

export type PendingItemId = number & { readonly __brand: "pending-item-id" };

const mintPendingId = (): PendingItemId => -++tempIdCounter as PendingItemId;

export const isPendingItem = (id: number): id is PendingItemId => id < 0;

type CachedAgendaItemList = { response?: unknown; data: AgendaItemList };

export type AgendaItemMutationVerb = "create" | "update" | "delete";

export interface AgendaItemMutationError {
  verb: AgendaItemMutationVerb;
  // HTTP status; null for network failures or cancellations (no response).
  status: number | null;
}

export interface UseAgendaItemMutationsParams {
  encounterId: number | undefined;
  patientId: number | undefined;
}

export interface UseAgendaItemMutationsResult {
  createAgendaItem: (description: string) => Promise<void>;
  toggleAgendaItem: (
    itemId: number,
    currentStatus: AgendaItemStatus,
  ) => Promise<void>;
  deleteAgendaItem: (itemId: number) => Promise<void>;
  error: AgendaItemMutationError | null;
}

export function useAgendaItemMutations({
  encounterId,
  patientId,
}: UseAgendaItemMutationsParams): UseAgendaItemMutationsResult {
  const { practice } = useActor();
  const queryClient = useQueryClient();
  // Tracked manually so any successful mutation clears the banner, even if
  // it's a different verb from the one that failed. React Query's per-hook
  // `isError` flag only resets when the *same* mutation is re-invoked.
  const [error, setError] = useState<AgendaItemMutationError | null>(null);

  type CreateMutationVars = {
    encounterId: number;
    data: { description: string };
  };

  type CreateContext =
    | { kind: "noop" }
    | {
        kind: "optimistic";
        previous: CachedAgendaItemList | undefined;
        tempId: PendingItemId;
      };

  const { mutateAsync: createMutation } = useCreateAgendaItem<
    CreateAgendaItemMutationError,
    CreateContext
  >({
    mutation: {
      onMutate: async (vars: CreateMutationVars): Promise<CreateContext> => {
        if (encounterId == null || patientId == null) {
          return { kind: "noop" };
        }

        const queryKey = getListAgendaItemsQueryKey(encounterId);
        await queryClient.cancelQueries({ queryKey });

        const previous =
          queryClient.getQueryData<CachedAgendaItemList>(queryKey);

        const tempId = mintPendingId();
        const now = new Date().toISOString();
        const placeholder: AgendaItem = {
          id: tempId,
          description: vars.data.description,
          type: "custom",
          status: "open",
          patientId,
          practiceId: practice.id,
          createdAt: now,
          updatedAt: now,
        };

        queryClient.setQueryData<CachedAgendaItemList>(queryKey, (old) => ({
          ...(old ?? {}),
          data: {
            items: [...(old?.data?.items ?? []), placeholder],
          },
        }));

        return { kind: "optimistic", previous, tempId };
      },
      onSuccess: (envelope, _vars, context) => {
        if (encounterId == null) return;
        if (!context || context.kind === "noop") return;
        const queryKey = getListAgendaItemsQueryKey(encounterId);
        const serverItem = envelope.data;
        const { tempId } = context;

        queryClient.setQueryData<CachedAgendaItemList>(queryKey, (old) => {
          const items = old?.data?.items ?? [];
          // Three orderings to reconcile:
          //   1. Real item is already in the cache (SSE-first race): drop
          //      the placeholder so the list ends up with exactly one copy.
          //   2. Placeholder is still in the cache (common case): swap it
          //      in-place for the server item.
          //   3. Neither placeholder nor real item is in the cache — e.g.
          //      an SSE snapshot after a reconnect replaced `items` before
          //      this callback ran. Append the server item so it is not
          //      silently dropped; the eventual `agenda_item.created` SSE
          //      event will dedup by id.
          const realAlreadyPresent = items.some((i) => i.id === serverItem.id);
          if (realAlreadyPresent) {
            return {
              ...(old ?? {}),
              data: { items: items.filter((i) => i.id !== tempId) },
            };
          }
          const placeholderPresent = items.some((i) => i.id === tempId);
          const next = placeholderPresent
            ? items.map((i) => (i.id === tempId ? serverItem : i))
            : [...items, serverItem];
          return {
            ...(old ?? {}),
            data: { items: next },
          };
        });
        setError(null);
      },
      onError: (err, vars, context) => {
        console.error("[useAgendaItemMutations] create failed", {
          encounterId,
          tempId: context?.kind === "optimistic" ? context.tempId : undefined,
          description: vars.data.description,
          error: err,
        });
        setError({ verb: "create", status: err.response?.status ?? null });
        if (encounterId == null) return;
        if (!context || context.kind === "noop") return;
        const queryKey = getListAgendaItemsQueryKey(encounterId);
        queryClient.setQueryData(queryKey, context.previous);
      },
      // No onSettled: deliberately skip invalidation to avoid a refetch that
      // races against SSE and re-introduces flicker. The SSE handler keeps
      // the cache current; the 5s polling refetchInterval (fallback mode)
      // catches any drift.
    },
  });

  type UpdateMutationVars = {
    encounterId: number;
    itemId: number;
    data: { status?: AgendaItemStatus };
  };

  type UpdateContext = {
    previous: CachedAgendaItemList | undefined;
  };

  const { mutateAsync: updateMutation } = useUpdateAgendaItem<
    UpdateAgendaItemMutationError,
    UpdateContext
  >({
    mutation: {
      onMutate: async (vars: UpdateMutationVars): Promise<UpdateContext> => {
        if (encounterId == null) return { previous: undefined };
        const queryKey = getListAgendaItemsQueryKey(encounterId);
        await queryClient.cancelQueries({ queryKey });
        const previous =
          queryClient.getQueryData<CachedAgendaItemList>(queryKey);
        queryClient.setQueryData<CachedAgendaItemList>(queryKey, (old) => ({
          ...(old ?? {}),
          data: {
            items: (old?.data?.items ?? []).map((i) =>
              i.id === vars.itemId ? { ...i, ...vars.data } : i,
            ),
          },
        }));
        return { previous };
      },
      onSuccess: (envelope, vars) => {
        setError(null);
        if (encounterId == null) return;
        const serverItem = envelope.data;
        const queryKey = getListAgendaItemsQueryKey(encounterId);
        queryClient.setQueryData<CachedAgendaItemList>(queryKey, (old) => ({
          ...(old ?? {}),
          data: {
            items: (old?.data?.items ?? []).map((i) =>
              i.id === vars.itemId ? serverItem : i,
            ),
          },
        }));
      },
      onError: (err, vars, context) => {
        console.error("[useAgendaItemMutations] update failed", {
          encounterId,
          itemId: vars.itemId,
          status: vars.data.status,
          error: err,
        });
        setError({ verb: "update", status: err.response?.status ?? null });
        if (encounterId == null) return;
        if (!context) return;
        const queryKey = getListAgendaItemsQueryKey(encounterId);
        queryClient.setQueryData(queryKey, context.previous);
      },
    },
  });

  type DeleteMutationVars = {
    encounterId: number;
    itemId: number;
  };

  type DeleteContext = {
    previous: CachedAgendaItemList | undefined;
  };

  const { mutateAsync: deleteMutation } = useDeleteAgendaItem<
    DeleteAgendaItemMutationError,
    DeleteContext
  >({
    mutation: {
      onMutate: async (vars: DeleteMutationVars): Promise<DeleteContext> => {
        if (encounterId == null) return { previous: undefined };
        const queryKey = getListAgendaItemsQueryKey(encounterId);
        await queryClient.cancelQueries({ queryKey });
        const previous =
          queryClient.getQueryData<CachedAgendaItemList>(queryKey);
        queryClient.setQueryData<CachedAgendaItemList>(queryKey, (old) => ({
          ...(old ?? {}),
          data: {
            items: (old?.data?.items ?? []).filter((i) => i.id !== vars.itemId),
          },
        }));
        return { previous };
      },
      onSuccess: () => {
        setError(null);
      },
      onError: (err, vars, context) => {
        console.error("[useAgendaItemMutations] delete failed", {
          encounterId,
          itemId: vars.itemId,
          error: err,
        });
        setError({ verb: "delete", status: err.response?.status ?? null });
        if (encounterId == null) return;
        if (!context) return;
        const queryKey = getListAgendaItemsQueryKey(encounterId);
        queryClient.setQueryData(queryKey, context.previous);
      },
    },
  });

  const createAgendaItem = async (description: string) => {
    if (encounterId == null || patientId == null) return;
    await createMutation({ encounterId, data: { description } });
  };

  const toggleAgendaItem = async (
    itemId: number,
    currentStatus: AgendaItemStatus,
  ) => {
    if (encounterId == null) return;
    const isCompleted =
      currentStatus === "finalized" || currentStatus === "was_discussed";
    const nextStatus: AgendaItemStatus = isCompleted ? "open" : "finalized";
    if (nextStatus === currentStatus) return;
    await updateMutation({ encounterId, itemId, data: { status: nextStatus } });
  };

  const deleteAgendaItem = async (itemId: number) => {
    if (encounterId == null) return;
    await deleteMutation({ encounterId, itemId });
  };

  return {
    createAgendaItem,
    toggleAgendaItem,
    deleteAgendaItem,
    error,
  };
}
